import { designVariables } from "@studio3t/design-variables";
import {
  BadgeInfoSubtle,
  type DropdownFooterItem,
  GenericDropdownWithSearch,
  GenericDropdownWithSearchContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useCallback, useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { jobsApi, workspacesApi } from "@/api/client";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { useGuardedNavigate } from "@/components/LeaveGuard";
import type { JobMetadata, WorkspaceSummary } from "@/types/api";
import { buildOpenPipelineItems } from "./openPipelineList";
import { openPipelineLabel } from "./pipelineEdits";
import { useStudioPipeline } from "./StudioPipelineProvider";

// The "Open pipeline" dropdown (Figma 14894-169764): saved workspaces and
// deployed jobs in one searchable list (GenericDropdownWithSearch's built-in
// filter), plus a link to the full Pipelines page pinned below the list as
// the dropdown's footerItem — it floats over the scrolling rows and the
// search filter never hides it. The trigger shows the currently open
// pipeline's name — starred while the editor holds unsaved local edits
// (Figma 14896-124011) — or the generic label at point zero.

// Workspaces + jobs, fetched when the dropdown opens. Same silent-keep
// pattern as WorkspaceControls' useWorkspaceList: an unreachable list keeps
// whatever was last fetched rather than clearing the menu underneath the
// user.
function useOpenPipelineLists(open: boolean) {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [jobs, setJobs] = useState<JobMetadata[]>([]);

  const refresh = useCallback(async () => {
    try {
      const [ws, js] = await Promise.all([
        workspacesApi.list(),
        jobsApi.list(),
      ]);
      setWorkspaces(ws);
      setJobs(js);
    } catch {
      // Unreachable lists keep the last known state.
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  return { workspaces, jobs };
}

// The trigger sits inside GenericDropdownWithSearch's own context provider
// (wrapping its `children`), so this is the only place the open flag is
// readable — it's relayed up to the menu via onOpenChange so the list fetch
// (which feeds the dropdownItems prop one level up) can watch it.
function OpenPipelineTrigger({
  label,
  onOpenChange,
}: {
  label: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { open } = useContext(GenericDropdownWithSearchContext);
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  return (
    <SecondaryTrigger
      type="button"
      $active={open}
      data-testid="open-pipeline-menu"
    >
      <IconSVG
        icon={Icon.Trace}
        size={BUTTON.iconsize}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
      {label}
      <IconSVG
        icon={Icon.BaseChevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
    </SecondaryTrigger>
  );
}

const NO_PIPELINES_LABEL = "No pipelines found";

export function OpenPipelineMenu(): JSX.Element {
  const { openPipeline, openEdited, openDraft, openJob } = useStudioPipeline();
  const navigate = useGuardedNavigate();
  const [open, setOpen] = useState(false);
  const { workspaces, jobs } = useOpenPipelineLists(open);

  const name =
    openPipeline === null
      ? null
      : openPipeline.kind === "draft"
        ? openPipeline.workspace.name
        : openPipeline.job.name;
  const label = openPipelineLabel(name, openEdited);

  const items = buildOpenPipelineItems(workspaces, jobs);

  // No closeOnSelect: the searchable dropdown closes on select anyway, and
  // it forwards the flag to the row's DOM node — a React unknown-prop error
  // on every open.
  //
  // Two "No pipelines found" placeholders, one per empty case: the
  // dropdown's own noItemsLabel row covers a search that matches nothing,
  // but with no search it renders nothing for an empty list — so a
  // disabled fallback row (isFallbackItem: shown only when the list has
  // no regular rows) covers the no-pipelines-at-all case. Same chrome as
  // the library's row: Info icon, disabled.
  const rows: ListItemProps[][] = [
    items.length === 0
      ? [
          {
            id: "no-pipelines",
            type: "text" as const,
            value: "no-pipelines",
            label: NO_PIPELINES_LABEL,
            leadingIcon: Icon.Info,
            disabled: true,
            isFallbackItem: true,
          },
        ]
      : items.map((item) => ({
          // Two workspaces may share a name, so a draft row is keyed by the
          // workspace it opens; job names are unique by definition.
          id:
            item.kind === "draft"
              ? `draft:${item.workspaceId}`
              : `job:${item.name}`,
          type: "text" as const,
          value: item.name,
          label: item.name,
          trailingIcon:
            item.kind === "draft" ? (
              <BadgeInfoSubtle>draft</BadgeInfoSubtle>
            ) : (
              <JobStatusBadge state={item.state} />
            ),
          onClick: () =>
            void (item.kind === "draft" ? openDraft(item) : openJob(item.name)),
        })),
  ];

  const footerItem: DropdownFooterItem = {
    id: "goto-pipelines",
    type: "text",
    value: "goto",
    label: "Go to the pipeline page",
    onClick: () => navigate("/pipelines"),
  };

  return (
    <GenericDropdownWithSearch
      dropdownItems={rows}
      footerItem={footerItem}
      inputAsTrigger={false}
      inputPlaceholder="Filter by pipeline name"
      noItemsLabel={NO_PIPELINES_LABEL}
      placement="bottom-start"
      elWidth={{ strategy: "fixed", size: 300 }}
    >
      <OpenPipelineTrigger label={label} onOpenChange={setOpen} />
    </GenericDropdownWithSearch>
  );
}

// The M-size trigger recipe (10px BaseChevron — the prebuilt derivatives
// render every icon at 16px, which oversizes the chevron). It lives here:
// the dropdown trigger is the only thing in the header still wearing it.
const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

const triggerBase = css`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
`;

const SecondaryTrigger = styled.button<{ $active?: boolean }>`
  ${triggerBase};
  color: ${(p) => p.theme.buttonSecondaryDefaultText};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonSecondaryActiveBackground
      : p.theme.buttonSecondaryDefaultBackground};
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme.buttonSecondaryActiveBorder
        : p.theme.buttonSecondaryDefaultBorder};

  &:hover {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
    border-color: ${(p) => p.theme.buttonSecondaryHoverBorder};
  }
`;
